import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, Loading, EmptyState, StatCard, StatusBadge } from "@/components/common";
import { rupiah, formatDate } from "@/lib/format";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Wallet, AlertTriangle, CalendarClock, Clock, TrendingDown } from "lucide-react";

const AGING = ["Belum Jatuh Tempo", "1-30 Hari", "31-60 Hari", "61-90 Hari", "> 90 Hari"];

export default function AccountsPayable() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [filters, setFilters] = useState({ supplier_id: "", status: "", aging: "" });

  const load = () => {
    setData(null);
    const q = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) q.set(k, v); });
    api.get(`/payables/accounts-payable?${q.toString()}`).then((r) => setData(r.data));
  };
  useEffect(load, [filters]);
  useEffect(() => { api.get("/payables/suppliers").then((r) => setSuppliers(r.data)); }, []);

  const s = data?.summary || {};
  return (
    <div>
      <PageHeader title="Hutang Usaha (Accounts Payable)" subtitle="Ringkasan hutang dari tagihan supplier yang telah disetujui. PO/GR tidak dihitung sebagai hutang." testId="ap-header" />
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6 mb-6">
        <StatCard label="Total Hutang" value={rupiah(s.total_hutang)} icon={Wallet} accent="indigo" testId="ap-total" />
        <StatCard label="Belum Jatuh Tempo" value={rupiah(s.belum_jatuh_tempo)} icon={CalendarClock} accent="slate" />
        <StatCard label="Jatuh Tempo Hari Ini" value={rupiah(s.jatuh_tempo_hari_ini)} icon={Clock} accent="amber" />
        <StatCard label="Jatuh Tempo 7 Hari" value={rupiah(s.jatuh_tempo_7_hari)} icon={CalendarClock} accent="sky" />
        <StatCard label="Terlambat" value={rupiah(s.terlambat)} icon={AlertTriangle} accent="rose" testId="ap-overdue" />
        <StatCard label="Dibayar Sebagian" value={rupiah(s.dibayar_sebagian)} icon={TrendingDown} accent="amber" />
      </div>
      <div className="mb-4 flex flex-wrap gap-2">
        <Select value={filters.supplier_id || "all"} onValueChange={(v) => setFilters({ ...filters, supplier_id: v === "all" ? "" : v })}>
          <SelectTrigger className="w-56" data-testid="ap-filter-supplier"><SelectValue placeholder="Semua Supplier" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua Supplier</SelectItem>{suppliers.map((x) => <SelectItem key={x.id} value={x.id}>{x.nama}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.status || "all"} onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}>
          <SelectTrigger className="w-48" data-testid="ap-filter-status"><SelectValue placeholder="Semua Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua Status</SelectItem><SelectItem value="Disetujui">Disetujui</SelectItem><SelectItem value="Dibayar Sebagian">Dibayar Sebagian</SelectItem><SelectItem value="Lunas">Lunas</SelectItem></SelectContent>
        </Select>
        <Select value={filters.aging || "all"} onValueChange={(v) => setFilters({ ...filters, aging: v === "all" ? "" : v })}>
          <SelectTrigger className="w-48" data-testid="ap-filter-aging"><SelectValue placeholder="Semua Aging" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua Aging</SelectItem>{AGING.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
        {!data ? <Loading /> : data.rows.length === 0 ? <EmptyState title="Tidak ada hutang" subtitle="Belum ada tagihan supplier yang disetujui." icon={Wallet} /> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>No Tagihan</TableHead><TableHead>Supplier</TableHead><TableHead>Tanggal</TableHead><TableHead>Jatuh Tempo</TableHead>
              <TableHead className="text-right">Total</TableHead><TableHead className="text-right">Dibayar</TableHead><TableHead className="text-right">Sisa</TableHead>
              <TableHead className="text-right">Telat (hari)</TableHead><TableHead>Aging</TableHead><TableHead>Status</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {data.rows.map((r) => (
                <TableRow key={r.bill_id} data-testid={`ap-row-${r.bill_no}`} className="cursor-pointer hover:bg-slate-50" onClick={() => nav(`/payables/bills?bill=${r.bill_id}`)}>
                  <TableCell className="font-mono text-xs">{r.bill_no}</TableCell>
                  <TableCell>{r.supplier_name}</TableCell>
                  <TableCell className="text-xs text-slate-500">{formatDate(r.bill_date)}</TableCell>
                  <TableCell className="text-xs text-slate-500">{formatDate(r.due_date)}</TableCell>
                  <TableCell className="text-right tabular-nums">{rupiah(r.grand_total)}</TableCell>
                  <TableCell className="text-right tabular-nums text-emerald-600">{rupiah(r.total_paid)}</TableCell>
                  <TableCell className="text-right tabular-nums font-semibold text-rose-600">{rupiah(r.outstanding)}</TableCell>
                  <TableCell className="text-right text-xs">{r.days_overdue > 0 ? r.days_overdue : "-"}</TableCell>
                  <TableCell><StatusBadge status={r.aging_bucket} /></TableCell>
                  <TableCell><StatusBadge status={r.status} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
